import React, { useState, useCallback, useRef } from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';

export default function SearchBox({ onSearch, placeholder = 'Search your collection...', defaultValue = '' }) {
  const [query, setQuery] = useState(defaultValue);
  const debounceRef = useRef(null);

  const handleChange = useCallback((e) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      // Auto-search requires 3+ characters, or empty to clear. isSubmit:
      // false tells callers this is a debounced keystroke pause, not an
      // explicit Enter — the generated LLM answer must not fire for it
      // (COL-95: that call is expensive and was previously firing on every
      // debounce pause).
      if (value.length >= 3 || value.length === 0) {
        onSearch(value, { isSubmit: false });
      }
    }, 750);
  }, [onSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // Submit searches with any length (user explicitly pressed Enter) and
    // is the only trigger allowed to request the generated LLM answer.
    onSearch(query, { isSubmit: true });
  };

  const handleClear = () => {
    setQuery('');
    onSearch('', { isSubmit: false });
  };

  return (
    <form className="search-box" onSubmit={handleSubmit}>
      <span className="search-icon">
        <SearchIcon />
      </span>
      <input
        type="text"
        className="search-input"
        placeholder={placeholder}
        value={query}
        onChange={handleChange}
        style={{
          paddingRight: query ? '2.5rem' : 'var(--space-md)',
        }}
      />
      {query && (
        <button
          type="button"
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: 'var(--space-sm)',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none',
          }}
        >
          <CloseIcon />
        </button>
      )}
    </form>
  );
}

function SearchIcon() {
  return <MagnifyingGlass size={18} />;
}

function CloseIcon() {
  return <X size={14} />;
}
